"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

interface QueryProviderProps {
	children: ReactNode;
}

// Export a function to create query client with consistent settings
export function createQueryClient() {
	return new QueryClient({
		defaultOptions: {
			queries: {
				// Use DYNAMIC tier as default for client-side caching
				// This matches server-side defaults and provides good performance
				// Individual hooks can override for STATIC (longer) or REALTIME (no cache)
				staleTime: 30 * 1000, // 30s (DYNAMIC tier default)
				gcTime: 10 * 60 * 1000, // 10min (DYNAMIC tier default)
				// Retry failed requests
				retry: (failureCount, error) => {
					// Don't retry on 4xx errors
					if (error && typeof error === "object" && "status" in error) {
						const status = error.status as number;
						if (status >= 400 && status < 500) {
							return false;
						}
					}
					return failureCount < 3;
				},
				// Refetch on window focus for important data
				refetchOnWindowFocus: false,
				// Background refetching
				refetchOnReconnect: true,
				// TanStack Query v5 Enhanced Features
				throwOnError: false, // Let error boundaries handle errors when needed
				notifyOnChangeProps: "all", // Better optimization for re-renders
				// Enhanced network mode handling
				networkMode: "online", // Default behavior but explicit
				// Better error retry delays
				retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
			},
			mutations: {
				// Global error handling for mutations
				onError: (error) => {
					console.error("Mutation error:", error);
				},
				// Enhanced retry for mutations
				retry: (failureCount, error) => {
					// Retry network errors but not 4xx client errors
					if (error && typeof error === "object" && "status" in error) {
						const status = error.status as number;
						if (status >= 400 && status < 500) {
							return false;
						}
					}
					return failureCount < 2; // Retry mutations more conservatively
				},
				// Enhanced mutation retry delays
				retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
			},
		},
	});
}

export function QueryProvider({ children }: QueryProviderProps) {
	const [queryClient] = useState(() => createQueryClient());

	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
